import { API_BASE_URL } from "../config/apiConfig";
import {
  useEffect,
  useMemo,
  useState,
} from "react";

import PublicLayout from "../layouts/PublicLayout";

const API_BASE =
  API_BASE_URL;

const formatDate = (
  value
) => {
  if (!value) {
    return "";
  }

  const date =
    new Date(value);

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return "";
  }

  return date.toLocaleDateString(
    "en-CA",
    {
      year: "numeric",
      month: "long",
      day: "numeric",
    }
  );
};

function NewsInsights() {
  const [
    articles,
    setArticles,
  ] = useState([]);

  const [
    categories,
    setCategories,
  ] = useState([]);

  const [
    activeCategory,
    setActiveCategory,
  ] = useState("All");

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    error,
    setError,
  ] = useState("");

  /* ========================================
     LOAD HEALTHCARE NEWS
  ======================================== */

  useEffect(() => {
    const loadHealthcareNews =
      async () => {
        try {
          setLoading(true);
          setError("");

          const response =
            await fetch(
              `${API_BASE}/healthcare-news?limit=50`
            );

          const data =
            await response.json();

          if (!response.ok) {
            throw new Error(
              data.message ||
                "Unable to load Healthcare Hub updates."
            );
          }

          const loadedArticles =
            Array.isArray(
              data.articles
            )
              ? data.articles
              : [];

          setArticles(
            loadedArticles
          );

          const uniqueCategories =
            [
              ...new Set(
                loadedArticles
                  .map(
                    (article) =>
                      article.category
                  )
                  .filter(Boolean)
              ),
            ];

          setCategories(
            uniqueCategories
          );
        } catch (loadError) {
          console.error(
            "Healthcare Hub error:",
            loadError
          );

          setError(
            loadError.message ||
              "Unable to load Healthcare Hub updates."
          );
        } finally {
          setLoading(false);
        }
      };

    loadHealthcareNews();
  }, []);

  /* ========================================
     FILTERED ARTICLES
  ======================================== */

  const filteredArticles =
    useMemo(() => {
      if (
        activeCategory ===
        "All"
      ) {
        return articles;
      }

      return articles.filter(
        (article) =>
          article.category ===
          activeCategory
      );
    }, [
      articles,
      activeCategory,
    ]);

  /* ========================================
     FEATURED ARTICLE
  ======================================== */

  const featuredArticle =
    useMemo(() => {
      const featured =
        filteredArticles.find(
          (article) =>
            article.is_featured
        );

      return (
        featured ||
        filteredArticles[0] ||
        null
      );
    }, [
      filteredArticles,
    ]);

  const remainingArticles =
    useMemo(() => {
      if (
        !featuredArticle
      ) {
        return [];
      }

      return filteredArticles.filter(
        (article) =>
          article.id !==
          featuredArticle.id
      );
    }, [
      filteredArticles,
      featuredArticle,
    ]);

  /* ========================================
     PAGE
  ======================================== */

  return (
    <PublicLayout>
      <section className="news-hero">
        <div className="news-container">
          <p className="section-label">
            Zeth Healthcare Hub
          </p>

          <h1>
            Updates & Knowledge for
            Professionals and Facilities
          </h1>

          <p className="news-hero-copy">
            Stay informed with the latest
            Canadian healthcare news,
            nursing updates, staffing
            trends, facility management
            insights, career resources,
            regulation updates and
            digital-health developments.
          </p>
        </div>
      </section>

      <section className="news-section">
        <div className="news-container">

          {/* ========================================
              FILTERS
          ======================================== */}

          <div className="news-filters">
            <button
              type="button"
              className={`news-filter ${
                activeCategory ===
                "All"
                  ? "active"
                  : ""
              }`}
              onClick={() =>
                setActiveCategory(
                  "All"
                )
              }
            >
              All
            </button>

            {categories.map(
              (category) => (
                <button
                  key={category}
                  type="button"
                  className={`news-filter ${
                    activeCategory ===
                    category
                      ? "active"
                      : ""
                  }`}
                  onClick={() =>
                    setActiveCategory(
                      category
                    )
                  }
                >
                  {category}
                </button>
              )
            )}
          </div>

          {/* ========================================
              LOADING
          ======================================== */}

          {loading && (
            <div className="news-state">
              <div className="news-loader" />

              <p>
                Loading Healthcare Hub
                updates...
              </p>
            </div>
          )}

          {/* ========================================
              ERROR
          ======================================== */}

          {!loading &&
            error && (
              <div className="news-state news-error">
                <h2>
                  Healthcare Hub is
                  temporarily unavailable
                </h2>

                <p>
                  {error}
                </p>

                <button
                  type="button"
                  className="primary-button"
                  onClick={() =>
                    window.location.reload()
                  }
                >
                  Try Again
                </button>
              </div>
            )}

          {/* ========================================
              EMPTY STATE
          ======================================== */}

          {!loading &&
            !error &&
            filteredArticles.length ===
              0 && (
              <div className="news-state">
                <h2>
                  No updates available
                </h2>

                <p>
                  New Healthcare Hub
                  articles will appear here
                  as they are published.
                </p>
              </div>
            )}

          {/* ========================================
              FEATURED ARTICLE
          ======================================== */}

          {!loading &&
            !error &&
            featuredArticle && (
              <article className="news-featured-card">
                <div className="news-featured-content">
                  <div>
                    <span className="news-category">
                      {featuredArticle.category ||
                        "Healthcare"}
                    </span>

                    <h2>
                      {
                        featuredArticle.title
                      }
                    </h2>

                    <p>
                      {
                        featuredArticle.summary
                      }
                    </p>

                    <div className="news-meta">
                      <span>
                        {featuredArticle.source_name ||
                          "Zeth Health"}
                      </span>

                      <span>
                        {formatDate(
                          featuredArticle.published_at ||
                            featuredArticle.created_at
                        )}
                      </span>
                    </div>

                    {featuredArticle.original_url && (
                      <a
                        className="news-link"
                        href={
                          featuredArticle.original_url
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Read full article →
                      </a>
                    )}
                  </div>
                </div>
              </article>
            )}

          {/* ========================================
              ARTICLE GRID
          ======================================== */}

          {!loading &&
            !error &&
            remainingArticles.length >
              0 && (
              <>
                <div className="news-section-heading">
                  <div>
                    <p className="section-label">
                      Latest Updates
                    </p>

                    <h2>
                      Healthcare News &
                      Insights
                    </h2>
                  </div>
                </div>

                <div className="news-grid">
                  {remainingArticles.map(
                    (article) => (
                      <article
                        className="news-card"
                        key={
                          article.id ||
                          article.title
                        }
                      >
                        <span className="news-category">
                          {article.category ||
                            "Healthcare"}
                        </span>

                        <h2>
                          {article.title}
                        </h2>

                        <p>
                          {article.summary}
                        </p>

                        <div className="news-meta">
                          <span>
                            {article.source_name ||
                              "Zeth Health"}
                          </span>

                          <span>
                            {formatDate(
                              article.published_at ||
                                article.created_at
                            )}
                          </span>
                        </div>

                        {article.original_url ? (
                          <a
                            className="news-link"
                            href={
                              article.original_url
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Read article →
                          </a>
                        ) : (
                          <span className="news-link disabled">
                            Article link
                            unavailable
                          </span>
                        )}
                      </article>
                    )
                  )}
                </div>
              </>
            )}
        </div>
      </section>
    </PublicLayout>
  );
}

export default NewsInsights;
